import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MinimizeIcon from "@mui/icons-material/Minimize";
import { Grid } from "@mui/material";
import React, { useContext } from "react";

import { HasbotContext } from "../../context/hasBot-context";
import { createTelemetryEvent } from "../../telemetry/Telemetry";
import { telemetryChannelName } from "../../util/constants";
import { PageHeader, StyledHeaderIconButton } from "./Common.elements";

type HelpAndSupportHeaderProps = {
  readonly children: React.ReactElement;
  readonly onClose?: () => void;
};

export function HelpAndSupportHeader({ children, onClose }: Readonly<HelpAndSupportHeaderProps>) {
  const {
    setTechSupportActive,
    setZopimChatActive,
    setShowDialog,
    setAiChatActive,
    openDialogBtnRef,
    zopimChatActive,
  } = useContext(HasbotContext);

  const dispatchHoursEvent = () => {
    const zopimHours = new Event("updateHours");
    document.dispatchEvent(zopimHours);
  };

  const handleClick = () => {
    // Telemetry: Chat closed via back button (only if chat was active)
    if (zopimChatActive) {
      createTelemetryEvent({
        feature: "human sme chat",
        action: "closed",
      }).fire(telemetryChannelName);
    }

    setTechSupportActive(undefined);
    setZopimChatActive(false);
    setAiChatActive(false);
    dispatchHoursEvent();
  };

  const closeDialog = () => {
    onClose?.();
    // Telemetry: Chat closed (only if chat was active)
    if (zopimChatActive) {
      createTelemetryEvent({
        feature: "human sme chat",
        action: "closed",
      }).fire(telemetryChannelName);
    }

    setTechSupportActive(undefined);
    setZopimChatActive(false);
    setAiChatActive(false);
    setShowDialog(false);
    openDialogBtnRef.current?.focus();
    const endChat = new Event("endZopimChat");
    document.dispatchEvent(endChat);
    dispatchHoursEvent();
  };

  const minimizeDialog = () => {
    setShowDialog(false);
    openDialogBtnRef.current?.focus();
  };

  return (
    <PageHeader container id="header" direction="row" alignItems="center">
      <Grid sx={{ justifyContent: "flex-start" }}>
        <StyledHeaderIconButton
          className="loading-button"
          onClick={handleClick}
          aria-label="Go back to help and support main page"
        >
          <KeyboardArrowLeftIcon />
        </StyledHeaderIconButton>
      </Grid>

      <Grid container size={{ xs: 6 }} sx={{ flexGrow: 1 }} alignItems="center" aria-live="assertive">
        {children}
      </Grid>
      <Grid sx={{ justifyContent: "flex-end" }}>
        <StyledHeaderIconButton
          id="minimize-help-dialog"
          aria-label="Minimize help dialog"
          className="chat-minimize-button"
          onClick={minimizeDialog}
        >
          <MinimizeIcon />
        </StyledHeaderIconButton>
        <StyledHeaderIconButton
          id="close-help-dialog"
          aria-label="Close help dialog"
          className="chat-close-button"
          onClick={closeDialog}
        >
          <CloseIcon />
        </StyledHeaderIconButton>
      </Grid>
    </PageHeader>
  );
}
